import React, { useEffect, useRef, useState } from "react";
import { Images, X, ChevronLeft, ChevronRight, Download, Reply, Play } from "lucide-react";
import { toast } from "sonner";
import { imgUrl } from "../../lib/api";

export const customerMedia = (msgs) => {
  const out = [];
  msgs.filter((m) => m.sender === "visitor" && !m.deleted).forEach((m) => {
    const atts = m.attachments || [];
    (m.images || []).filter((u) => !atts.some((a) => a.url === u)).forEach((u, i) => out.push({ key: `${m.id}-l${i}`, url: u, kind: "image", name: u.split("/").pop(), m }));
    atts.filter((a) => a.kind === "image" || a.kind === "video").forEach((a) => out.push({ key: a.id, url: a.url, kind: a.kind, name: a.name, m }));
  });
  return out.reverse();
};

const when = (iso) => new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

const download = async (it) => {
  const res = await fetch(imgUrl(it.url));
  const blob = await res.blob();
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = it.name || `photo-${it.key}`;
  a.click();
  URL.revokeObjectURL(a.href);
};

const Thumb = ({ it, className = "" }) => (
  <span className={`relative block overflow-hidden rounded-lg bg-[#eef3f2] ${className}`}>
    {it.kind === "video" ? <video src={imgUrl(it.url)} muted preload="metadata" className="h-full w-full object-cover" /> : <img src={imgUrl(it.url)} alt={it.name} loading="lazy" className="h-full w-full object-cover" />}
    {it.kind === "video" && <span className="absolute inset-0 flex items-center justify-center"><span className="rounded-full bg-black/55 p-1.5 text-white"><Play size={13} className="fill-current" /></span></span>}
  </span>
);

export const GalleryStrip = ({ items, onOpen, onPanel }) => !items.length ? null : (
  <div className="flex items-center gap-2 border-b border-[#eef5f3] bg-white px-4 py-2" data-testid="gallery-strip">
    <div className="no-scrollbar flex flex-1 gap-1.5 overflow-x-auto">
      {items.map((it, i) => (
        <button key={it.key} onClick={() => onOpen(i)} className="shrink-0 transition-transform hover:scale-105" title={when(it.m.created_at)} data-testid="gallery-strip-thumb"><Thumb it={it} className="h-12 w-12" /></button>
      ))}
    </div>
    <button onClick={onPanel} className="flex shrink-0 items-center gap-1.5 rounded-full bg-[#0f2e2a] px-3 py-1.5 text-[12.5px] font-semibold text-white hover:bg-[#1c4640]" data-testid="gallery-open-btn"><Images size={14} />Photos ({items.length})</button>
  </div>
);

export const GalleryPanel = ({ open, items, name, onClose, onOpen, onReply }) => {
  const [busy, setBusy] = useState(false);
  if (!open) return null;
  const all = async () => {
    setBusy(true);
    try { for (const it of items) await download(it); toast.success(`Downloaded ${items.length} file(s)`); } catch { toast.error("Some files could not be downloaded"); }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-[70] flex justify-end bg-[#0f2e2a]/30" onClick={onClose} data-testid="gallery-panel">
      <div className="flex h-full w-full max-w-[440px] flex-col bg-white shadow-2xl sw-fade-up" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 border-b border-[#eef5f3] px-5 py-4">
          <Images size={18} className="text-[#0e8c7f]" />
          <div className="min-w-0 flex-1"><div className="truncate font-semibold text-[#0f2e2a]">{name}'s photos & videos</div><div className="text-[12.5px] text-[#6b7c79]">{items.length} item(s) · newest first</div></div>
          <button onClick={onClose} className="rounded-full p-1.5 hover:bg-[#f2f7f6]" aria-label="Close gallery" data-testid="gallery-close"><X size={18} /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          {!items.length && <p className="py-10 text-center text-[14px] text-[#6b7c79]">No photos or videos from this customer yet.</p>}
          <div className="grid grid-cols-2 gap-3">
            {items.map((it, i) => (
              <div key={it.key} className="overflow-hidden rounded-xl border border-[#e3f0ed]" data-testid="gallery-item">
                <button onClick={() => onOpen(i)} className="block w-full"><Thumb it={it} className="aspect-square w-full rounded-none" /></button>
                <div className="px-2.5 py-2">
                  <div className="text-[11.5px] text-[#6b7c79]" data-testid="gallery-item-date">{when(it.m.created_at)}</div>
                  <div className="mt-1.5 flex gap-1.5">
                    <button onClick={() => onReply(it.m)} className="flex flex-1 items-center justify-center gap-1 rounded-full bg-[#0e8c7f] py-1 text-[12px] font-semibold text-white hover:bg-[#0b7569]" data-testid="gallery-reply-btn"><Reply size={12} />Reply to this</button>
                    <button onClick={() => download(it)} className="rounded-full border border-[#dcebe8] p-1.5 text-[#0e8c7f] hover:bg-[#effaf7]" aria-label="Download" data-testid="gallery-download-btn"><Download size={13} /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        {items.length > 0 && (
          <div className="border-t border-[#eef5f3] p-4">
            <button onClick={all} disabled={busy} className="sw-btn-primary w-full justify-center !py-2.5 text-[14px] disabled:opacity-60" data-testid="gallery-download-all"><Download size={15} />{busy ? "Downloading…" : `Download all (${items.length})`}</button>
          </div>
        )}
      </div>
    </div>
  );
};

export const Lightbox = ({ items, index, setIndex, onReply }) => {
  const touch = useRef(null);
  const it = index != null ? items[index] : null;
  const go = (d) => setIndex((i) => (i + d + items.length) % items.length);
  useEffect(() => {
    if (index == null) return undefined;
    const key = (e) => { if (e.key === "ArrowRight") go(1); if (e.key === "ArrowLeft") go(-1); if (e.key === "Escape") setIndex(null); };
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  });
  if (!it) return null;
  return (
    <div className="fixed inset-0 z-[85] flex flex-col bg-black/90" onClick={() => setIndex(null)} data-testid="gallery-lightbox"
      onTouchStart={(e) => (touch.current = e.touches[0].clientX)} onTouchEnd={(e) => { const dx = e.changedTouches[0].clientX - (touch.current ?? 0); if (Math.abs(dx) > 50) go(dx < 0 ? 1 : -1); }}>
      <div className="flex items-center gap-3 px-5 py-3 text-white" onClick={(e) => e.stopPropagation()}>
        <span className="text-[13.5px]" data-testid="lightbox-counter">{index + 1} / {items.length} · {when(it.m.created_at)}</span>
        <span className="flex-1" />
        <button onClick={() => { onReply(it.m); setIndex(null); }} className="flex items-center gap-1.5 rounded-full bg-[#0e8c7f] px-3 py-1.5 text-[13px] font-semibold" data-testid="lightbox-reply-btn"><Reply size={14} />Reply to this</button>
        <button onClick={() => download(it)} className="rounded-full bg-white/15 p-2" aria-label="Download" data-testid="lightbox-download-btn"><Download size={17} /></button>
        <button onClick={() => setIndex(null)} className="rounded-full bg-white/15 p-2" aria-label="Close" data-testid="lightbox-close"><X size={18} /></button>
      </div>
      <div className="relative flex min-h-0 flex-1 items-center justify-center px-4 pb-6">
        {it.kind === "video"
          ? <video key={it.key} src={imgUrl(it.url)} controls autoPlay playsInline className="max-h-full max-w-full rounded-xl" onClick={(e) => e.stopPropagation()} />
          : <img key={it.key} src={imgUrl(it.url)} alt={it.name} className="max-h-full max-w-full rounded-xl object-contain" onClick={(e) => e.stopPropagation()} data-testid="lightbox-image" />}
        {items.length > 1 && (
          <>
            <button onClick={(e) => { e.stopPropagation(); go(-1); }} className="absolute left-3 rounded-full bg-white/15 p-2.5 text-white hover:bg-white/25" aria-label="Previous" data-testid="lightbox-prev"><ChevronLeft size={22} /></button>
            <button onClick={(e) => { e.stopPropagation(); go(1); }} className="absolute right-3 rounded-full bg-white/15 p-2.5 text-white hover:bg-white/25" aria-label="Next" data-testid="lightbox-next"><ChevronRight size={22} /></button>
          </>
        )}
      </div>
    </div>
  );
};
